import type { ManualLocale } from "./locale";

export type ManualUi = {
  manual: string;
  searchDocs: string;
  search: string;
  openNavigation: string;
  closeNavigation: string;
  allDocs: string;
  onThisPage: string;
  previous: string;
  next: string;
  startHere: string;
  browseFormats: string;
  allChapters: string;
  popularTopics: string;
  searchPlaceholder: string;
  searchLabel: string;
  searchDialog: string;
  expandSections: string;
  collapseSections: string;
  typeToSearch: string;
  noMatches: string;
  heroLead: string;
  language: string;
  switchToLight: string;
  switchToDark: string;
  logoAlt: string;
  documentTitle: string;
  chapterDocumentTitle: string;
  highlights: { title: string; text: string; to: string }[];
};

const highlightsTo = ["configs", "mapping", "map-view", "formats"] as const;

const copy: Record<ManualLocale, Omit<ManualUi, "highlights"> & { highlights: { title: string; text: string }[] }> = {
  en: {
    manual: "User Manual",
    searchDocs: "Search docs",
    search: "Search",
    openNavigation: "Open navigation",
    closeNavigation: "Close navigation",
    allDocs: "All SEPM documentation",
    onThisPage: "On this page",
    previous: "Previous",
    next: "Next",
    startHere: "Start here",
    browseFormats: "Browse formats",
    allChapters: "All chapters",
    popularTopics: "Popular topics",
    searchPlaceholder: "Search formats, mapping, account…",
    searchLabel: "Search",
    searchDialog: "Search documentation",
    expandSections: "Expand {title} sections",
    collapseSections: "Collapse {title} sections",
    typeToSearch: "Type to search the user manual.",
    noMatches: "No matches for “{query}”.",
    heroLead:
      "Practical guides for transferring geospatial and CAD data — formats, mapping, profiles, and SEPM account access.",
    language: "Language",
    switchToLight: "Switch to light mode",
    switchToDark: "Switch to dark mode",
    logoAlt: "SEPM logo",
    documentTitle: "SEPM X-Translator — User Manual",
    chapterDocumentTitle: "SEPM X-Translator Manual",
    highlights: [
      {
        title: "Configs & profiles",
        text: "Save model configurations, build Transfer Profiles, and reopen complete transfer setups.",
      },
      {
        title: "Mapping toolkit",
        text: "Connect, fixed/value mapping, Auto Map, Connect-ID / Connect-Delete — field by field.",
      },
      {
        title: "Map view & table",
        text: "Draw spatial filters, control layers, and inspect features in the Attribute Table.",
      },
      {
        title: "Formats",
        text: "PostGIS, AutoCAD, GeoPackage, Shapefile, KML, GeoJSON, CSV/Excel, X-Format, and more.",
      },
    ],
  },
  de: {
    manual: "Benutzerhandbuch",
    searchDocs: "Dokumentation suchen",
    search: "Suchen",
    openNavigation: "Navigation öffnen",
    closeNavigation: "Navigation schließen",
    allDocs: "Gesamte SEPM-Dokumentation",
    onThisPage: "Auf dieser Seite",
    previous: "Zurück",
    next: "Weiter",
    startHere: "Hier beginnen",
    browseFormats: "Formate ansehen",
    allChapters: "Alle Kapitel",
    popularTopics: "Häufige Themen",
    searchPlaceholder: "Formate, Zuordnung, Konto suchen…",
    searchLabel: "Suchen",
    searchDialog: "Dokumentation durchsuchen",
    expandSections: "Abschnitte von {title} ausklappen",
    collapseSections: "Abschnitte von {title} einklappen",
    typeToSearch: "Geben Sie einen Begriff ein, um das Handbuch zu durchsuchen.",
    noMatches: "Keine Treffer für „{query}“.",
    heroLead:
      "Praktische Anleitungen für den Transfer von Geodaten und CAD-Daten — Formate, Zuordnung, Profile und der SEPM-Kontozugang.",
    language: "Sprache",
    switchToLight: "Zum hellen Design wechseln",
    switchToDark: "Zum dunklen Design wechseln",
    logoAlt: "SEPM-Logo",
    documentTitle: "SEPM X-Translator — Benutzerhandbuch",
    chapterDocumentTitle: "SEPM X-Translator Handbuch",
    highlights: [
      {
        title: "Konfigurationen und Profile",
        text: "Modellkonfigurationen speichern, Transferprofile anlegen und vollständige Transfer-Setups wieder öffnen.",
      },
      {
        title: "Zuordnung",
        text: "Verbinden, feste und Wertzuordnung, Auto-Zuordnung, Connect-ID / Connect-Delete — Feld für Feld.",
      },
      {
        title: "Kartenansicht und Tabelle",
        text: "Räumliche Filter zeichnen, Layer steuern und Objekte in der Attributtabelle prüfen.",
      },
      {
        title: "Formate",
        text: "PostGIS, AutoCAD, GeoPackage, Shapefile, KML, GeoJSON, CSV/Excel, X-Format und weitere.",
      },
    ],
  },
  fr: {
    manual: "Manuel utilisateur",
    searchDocs: "Rechercher",
    search: "Rechercher",
    openNavigation: "Ouvrir la navigation",
    closeNavigation: "Fermer la navigation",
    allDocs: "Toute la documentation SEPM",
    onThisPage: "Sur cette page",
    previous: "Précédent",
    next: "Suivant",
    startHere: "Commencer ici",
    browseFormats: "Parcourir les formats",
    allChapters: "Tous les chapitres",
    popularTopics: "Sujets fréquents",
    searchPlaceholder: "Rechercher formats, correspondance, compte…",
    searchLabel: "Rechercher",
    searchDialog: "Rechercher dans la documentation",
    expandSections: "Développer les sections de {title}",
    collapseSections: "Réduire les sections de {title}",
    typeToSearch: "Saisissez un terme pour chercher dans le manuel.",
    noMatches: "Aucun résultat pour « {query} ».",
    heroLead:
      "Guides pratiques pour transférer des données géospatiales et CAO — formats, correspondance, profils et accès au compte SEPM.",
    language: "Langue",
    switchToLight: "Passer au mode clair",
    switchToDark: "Passer au mode sombre",
    logoAlt: "Logo SEPM",
    documentTitle: "SEPM X-Translator — Manuel utilisateur",
    chapterDocumentTitle: "Manuel SEPM X-Translator",
    highlights: [
      {
        title: "Configurations et profils",
        text: "Enregistrez les configurations de modèle, créez des profils de transfert et rouvrez une configuration complète.",
      },
      {
        title: "Correspondance",
        text: "Connecter, correspondance fixe ou de valeurs, Auto Map, Connect-ID / Connect-Delete — champ par champ.",
      },
      {
        title: "Carte et table",
        text: "Dessinez des filtres spatiaux, gérez les couches et inspectez les entités dans la table d’attributs.",
      },
      {
        title: "Formats",
        text: "PostGIS, AutoCAD, GeoPackage, Shapefile, KML, GeoJSON, CSV/Excel, X-Format, et d’autres.",
      },
    ],
  },
};

export function manualUi(locale: ManualLocale): ManualUi {
  const text = copy[locale];
  return {
    ...text,
    highlights: text.highlights.map((item, index) => ({
      ...item,
      to: highlightsTo[index],
    })),
  };
}
